import React from 'react'

/**
 * Статусные плашки и бейджи одним компонентом на роль.
 *
 * ЗАЧЕМ. В приложении 2 806 хардкод-классов палитры в 126 файлах. Из них
 * 1 441 (52 %) — это ровно две конструкции: плашка (`bg` + `border` + `text`,
 * 1 030 мест) и бейдж/чип (`bg` + `text`, 411 мест). По смыслу они сводятся
 * к пяти-шести ролям, а не к 157 различным наборам классов, в которые
 * разошлись за время жизни кода.
 *
 * Цвет живёт ВНУТРИ этих компонентов. Снаружи передаётся только `tone` —
 * что означает плашка, а не как она покрашена. Тогда правка палитры
 * статусов делается в одном файле, а не в 126.
 *
 * ⚠️ Цветной `className` снаружи не прокидывать. Это не стилистическое
 * пожелание: как только цвет снова начнёт приезжать строкой из вызывающего
 * кода, компонент перестанет быть единственным местом правки и миграция
 * протухнет. За этим следит правило `no-restricted-syntax` в .eslintrc.cjs,
 * включаемое по папкам по мере перевода домена.
 *
 * ТЁМНАЯ ТЕМА здесь отдельного кода не требует: палитровые лестницы
 * тематизированы (см. scripts/generate-theme-ramps.mjs), поэтому
 * `bg-amber-50 border-amber-200 text-amber-700` в тёмной теме сам
 * становится `bg-amber-950 border-amber-800 text-amber-300`.
 */

export type Tone = 'neutral' | 'info' | 'success' | 'warn' | 'danger' | 'accent'

/** Насколько громко звучит плашка. */
export type ToneEmphasis = 'soft' | 'strong'

/**
 * Лестница на роль. Значения выбраны по фактически преобладающим наборам
 * классов в коде, чтобы перевод существующих мест не менял их вид:
 * `bg-red-50 border-red-200 text-red-700` — 38 мест, `bg-amber-50
 * border-amber-200 text-amber-800` — 19, `bg-emerald-50 border-emerald-200
 * text-emerald-700` — 18.
 */
const RAMP: Record<Tone, string> = {
  neutral: 'slate',
  info: 'sky',
  success: 'emerald',
  warn: 'amber',
  danger: 'red',
  accent: 'violet',
}

const PANEL_CLASSES: Record<Tone, Record<ToneEmphasis, string>> = {
  neutral: { soft: 'bg-slate-50 border-slate-200 text-slate-700', strong: 'bg-slate-100 border-slate-300 text-slate-900' },
  info: { soft: 'bg-sky-50 border-sky-200 text-sky-700', strong: 'bg-sky-100 border-sky-300 text-sky-900' },
  success: { soft: 'bg-emerald-50 border-emerald-200 text-emerald-700', strong: 'bg-emerald-100 border-emerald-300 text-emerald-900' },
  warn: { soft: 'bg-amber-50 border-amber-200 text-amber-700', strong: 'bg-amber-100 border-amber-300 text-amber-900' },
  danger: { soft: 'bg-red-50 border-red-200 text-red-700', strong: 'bg-red-100 border-red-300 text-red-900' },
  accent: { soft: 'bg-violet-50 border-violet-200 text-violet-700', strong: 'bg-violet-100 border-violet-300 text-violet-900' },
}

const BADGE_CLASSES: Record<Tone, Record<ToneEmphasis, string>> = {
  neutral: { soft: 'bg-slate-100 text-slate-700', strong: 'bg-slate-200 text-slate-900' },
  info: { soft: 'bg-sky-100 text-sky-700', strong: 'bg-sky-200 text-sky-900' },
  success: { soft: 'bg-emerald-100 text-emerald-700', strong: 'bg-emerald-200 text-emerald-900' },
  warn: { soft: 'bg-amber-100 text-amber-700', strong: 'bg-amber-200 text-amber-900' },
  danger: { soft: 'bg-red-100 text-red-700', strong: 'bg-red-200 text-red-900' },
  accent: { soft: 'bg-violet-100 text-violet-700', strong: 'bg-violet-200 text-violet-900' },
}

const TEXT_CLASSES: Record<Tone, Record<ToneEmphasis, string>> = {
  neutral: { soft: 'text-slate-600', strong: 'text-slate-900' },
  info: { soft: 'text-sky-600', strong: 'text-sky-800' },
  success: { soft: 'text-emerald-600', strong: 'text-emerald-800' },
  warn: { soft: 'text-amber-600', strong: 'text-amber-800' },
  danger: { soft: 'text-red-600', strong: 'text-red-800' },
  accent: { soft: 'text-violet-600', strong: 'text-violet-800' },
}

/** Класс для точки/полосы статуса — заливка оттенком 500, он самозеркальный. */
const DOT_CLASSES: Record<Tone, string> = {
  neutral: 'bg-slate-500',
  info: 'bg-sky-500',
  success: 'bg-emerald-500',
  warn: 'bg-amber-500',
  danger: 'bg-red-500',
  accent: 'bg-violet-500',
}

/** Имя лестницы для случаев, где нужен нестандартный оттенок той же роли. */
export function toneRamp(tone: Tone): string {
  return RAMP[tone]
}

export function toneTextClass(tone: Tone, emphasis: ToneEmphasis = 'soft'): string {
  return TEXT_CLASSES[tone][emphasis]
}

export function toneDotClass(tone: Tone): string {
  return DOT_CLASSES[tone]
}

const PANEL_SIZE = {
  sm: 'rounded-md border px-2 py-1 text-[11px] leading-4',
  md: 'rounded-lg border px-3 py-2 text-xs leading-5',
  lg: 'rounded-lg border px-4 py-3 text-sm',
} as const

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  tone?: Tone
  emphasis?: ToneEmphasis
  size?: keyof typeof PANEL_SIZE
  children?: React.ReactNode
  /** ⚠️ Только геометрия и типографика. Цветные классы сюда не передавать. */
  className?: string
}

/** Плашка-предупреждение / пояснение: фон + рамка + текст одной роли. */
export function Panel({ tone = 'neutral', emphasis = 'soft', size = 'md', className = '', children, ...rest }: PanelProps) {
  return (
    <div className={`${PANEL_SIZE[size]} ${PANEL_CLASSES[tone][emphasis]} ${className}`} {...rest}>
      {children}
    </div>
  )
}

const BADGE_SIZE = {
  xs: 'rounded px-1 py-0.5 text-[9px] leading-3',
  sm: 'rounded px-1.5 py-0.5 text-[10px] leading-4',
  md: 'rounded-md px-2 py-0.5 text-xs leading-5',
  pill: 'rounded-full px-3 py-1 text-sm',
} as const

export interface ToneBadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  tone?: Tone
  emphasis?: ToneEmphasis
  size?: keyof typeof BADGE_SIZE
  children?: React.ReactNode
  /** ⚠️ Только геометрия и типографика. Цветные классы сюда не передавать. */
  className?: string
}

/**
 * Бейдж/чип статуса: фон + текст одной роли, без рамки.
 *
 * ⚠️ Имя `ToneBadge`, а не `Badge`: `Badge` уже занят в
 * `components/ui/Components.tsx` — там пилюля фиксированного размера на
 * семантических токенах (`bg-success/10`). Второй компонент с тем же именем
 * развёл бы вызывающий код по двум несовместимым API молча.
 */
export function ToneBadge({ tone = 'neutral', emphasis = 'soft', size = 'sm', className = '', children, ...rest }: ToneBadgeProps) {
  return (
    <span className={`inline-flex items-center font-medium ${BADGE_SIZE[size]} ${BADGE_CLASSES[tone][emphasis]} ${className}`} {...rest}>
      {children}
    </span>
  )
}

/** Точка статуса рядом с текстом. */
export function ToneDot({ tone = 'neutral', className = '' }: { tone?: Tone; className?: string }) {
  return <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[tone]} ${className}`} />
}

/* ------------------------------------------------------------------ */
/* Именованные шкалы, у которых свой набор оттенков                    */
/* ------------------------------------------------------------------ */

/**
 * Шкала исполнения плана: ≥ 100 % / 80–99 % / < 80 %.
 *
 * ⚠️ Живёт отдельно от общих ролей и НЕ сводится к `success`/`warn`/`danger`.
 * У неё свои лестницы — green и yellow, а не emerald и amber. Свести их
 * означало бы перекрасить показатели плана на всех экранах метрик
 * (#16a34a → #059669), то есть нарушить инвариант «светлая тема после
 * миграции выглядит как до неё». Оттенки здесь — ровно те, что были.
 */
export type PlanStatus = 'met' | 'close' | 'missed'

const PLAN_STATUS_TEXT: Record<PlanStatus, string> = {
  met: 'text-green-600',
  close: 'text-yellow-600',
  missed: 'text-red-600',
}

const PLAN_STATUS_SWATCH: Record<PlanStatus, string> = {
  met: 'bg-green-600',
  close: 'bg-yellow-600',
  missed: 'bg-red-600',
}

export function planStatusTextClass(status: PlanStatus): string {
  return PLAN_STATUS_TEXT[status]
}

export function planStatusSwatchClass(status: PlanStatus): string {
  return PLAN_STATUS_SWATCH[status]
}

/**
 * Мелкие акценты форм и иконок.
 *
 * Вынесены сюда не ради красоты, а чтобы цвет не жил строкой в вызывающем
 * файле: пока он там, ратчет для домена включить нельзя. Значения прежние
 * байт-в-байт; тематизируются они сами, через лестницы.
 * ⚠️ `text-error` вместо `text-red-500` здесь НЕ подставлен намеренно —
 * это другой красный (#F44336 против #EF4444), и подмена изменила бы вид.
 */
export const FORM_ERROR_TEXT_CLASS = 'text-red-500'
export const FORM_INVALID_FIELD_CLASS = 'border-orange-500 ring-2 ring-orange-200'
export const ICON_DANGER_CLASS = 'text-red-500'
